import React, { useState } from 'react';
import PageHero from './PageHero';
import {
  CreditCard,
  CalendarCheck,
  Clock,
  Sparkles,
  Percent,
  CheckCircle2,
  Info,
  Building2,
  ArrowRight,
  Briefcase,
  Layers
} from 'lucide-react';
import heroImg from '../assets/Banner-GMIS Students.png';

export default function FeesTuitionPage({ setPage, t }) {
  // Localization data from t with fallback
  const content = t?.schoolFees || {};
  const ts = content?.tableSection || {};
  const appFee = content?.applicationFee || {};
  const po = content?.paymentOptions || {};
  const disc = content?.discountsSection || {};
  const pol = content?.policies || {};
  const corp = content?.corporatePartnerships || {};
  const cta = content?.cta || {};

  const [stageFilter, setStageFilter] = useState('all');
  const [paymentPlanView, setPaymentPlanView] = useState('annual'); // 'annual', 'quarterly', 'monthly'

  const rows = ts?.rows || [
    {
      stage: 'Preschool',
      stageKey: 'preschool',
      group: 'Toddler',
      fee: 'Rp. 70,000,000',
      rawFee: 70000000,
      badgeColor: 'bg-amber-500/10 text-amber-700 border-amber-200'
    },
    {
      stage: 'Preschool',
      stageKey: 'preschool',
      group: 'Nursery',
      fee: 'Rp. 94,245,000',
      rawFee: 94245000,
      badgeColor: 'bg-amber-500/10 text-amber-700 border-amber-200'
    },
    {
      stage: 'Preschool',
      stageKey: 'preschool',
      group: 'Kindergarten',
      fee: 'Rp. 98,365,000',
      rawFee: 98365000,
      badgeColor: 'bg-amber-500/10 text-amber-700 border-amber-200'
    },
    {
      stage: 'Preschool',
      stageKey: 'preschool',
      group: 'Preparatory',
      fee: 'Rp. 102,485,000',
      rawFee: 102485000,
      badgeColor: 'bg-amber-500/10 text-amber-700 border-amber-200'
    },
    {
      stage: 'Primary School',
      stageKey: 'primary',
      group: 'Primary School (Grade 1–5)',
      fee: 'Rp. 131,325,000',
      rawFee: 131325000,
      badgeColor: 'bg-blue-500/10 text-blue-700 border-blue-200'
    },
    {
      stage: 'Middle School',
      stageKey: 'middle',
      group: 'Middle School (Grade 6–8)',
      fee: 'Rp. 154,500,000',
      rawFee: 154500000,
      badgeColor: 'bg-emerald-500/10 text-emerald-700 border-emerald-200'
    },
    {
      stage: 'Senior School',
      stageKey: 'senior',
      group: 'Senior School (Grade 9–10)',
      fee: 'Rp. 181,280,000',
      rawFee: 181280000,
      badgeColor: 'bg-indigo-500/10 text-indigo-700 border-indigo-200'
    },
    {
      stage: 'Senior School',
      stageKey: 'senior',
      group: 'Senior School (Grade 11–12)',
      fee: 'Rp. 204,970,000',
      rawFee: 204970000,
      badgeColor: 'bg-purple-500/10 text-purple-700 border-purple-200'
    }
  ];

  const filteredRows = rows.filter(item => {
    if (stageFilter === 'all') return true;
    const itemStage = (item.stageKey || item.stage || '').toLowerCase();
    return itemStage.includes(stageFilter);
  });

  const formatCurrency = (val) => {
    return 'Rp. ' + Math.round(val).toLocaleString('id-ID');
  };

  const getComputedFeeDisplay = (rawFee, defaultFeeStr) => {
    if (!rawFee) return defaultFeeStr;
    if (paymentPlanView === 'quarterly') {
      return formatCurrency(rawFee / 4);
    }
    if (paymentPlanView === 'monthly') {
      return formatCurrency(rawFee / 10);
    }
    return defaultFeeStr;
  };

  const handleNavigateToContact = () => {
    if (setPage) setPage('contact');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleNavigateToFaqs = () => {
    if (setPage) setPage('faqs');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="w-full bg-[#f8f9fa] min-h-screen text-ink">
      {/* ── Page Hero ────────────────────────────────────────────── */}
      <PageHero
        title={content.title || 'School Fees & Tuition'}
        eyebrow={content.eyebrow || 'ADMISSIONS'}
        subtitle={content.subtitle || 'Clear, transparent tuition and flexible payment plans designed to support families investing in their child’s future.'}
        breadcrumbs={content.breadcrumbs || ['Home', 'Admissions', 'Fees & Tuition']}
        bgImage={heroImg}
      />

      {/* ── Top Floating Action Bar ──────────────────────────────────── */}
      <section className="relative -mt-10 z-20 max-w-[1240px] mx-auto px-6 box-border">
        <div className="bg-white rounded-2xl p-5 lg:p-7 shadow-xl border border-line flex flex-col lg:flex-row items-center justify-between gap-5">
          <div className="flex items-center gap-4 w-full lg:w-auto">
            <div className="w-13 h-13 rounded-2xl bg-gold/15 flex items-center justify-center text-gold-ink shrink-0">
              <CreditCard className="w-7 h-7 text-gold-ink" />
            </div>
            <div>
              <div className="text-[11px] font-bold tracking-[0.2em] text-gold uppercase mb-1">
                {content.academicYear || 'Academic Year 2027–2028'}
              </div>
              <h2 className="text-xl md:text-2xl font-heading font-bold text-navy m-0">
                Transparent & Flexible School Fees
              </h2>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3 w-full lg:w-auto">
            <button
              onClick={() => scrollToSection('fees-table')}
              className="btn flex-1 sm:flex-initial inline-flex items-center justify-center gap-2 h-11 px-5 rounded-xl bg-gold text-navy-deep font-heading font-bold text-[13px] tracking-wider uppercase transition-all shadow-md hover:shadow-lg"
            >
              <span>Annual Fees Table</span>
              <ArrowRight className="w-4 h-4" />
            </button>
            <button
              onClick={() => scrollToSection('payment-options')}
              className="btn flex-1 sm:flex-initial inline-flex items-center justify-center gap-2 h-11 px-5 rounded-xl border border-navy/20 bg-paper text-navy font-heading font-bold text-[13px] tracking-wider uppercase hover:bg-white transition-all"
            >
              <CalendarCheck className="w-4 h-4 text-gold-ink" />
              <span>Payment Options</span>
            </button>
            <button
              onClick={() => scrollToSection('discounts')}
              className="btn flex-1 sm:flex-initial inline-flex items-center justify-center gap-2 h-11 px-5 rounded-xl border border-navy/20 bg-paper text-navy font-heading font-bold text-[13px] tracking-wider uppercase hover:bg-white transition-all"
            >
              <Percent className="w-4 h-4 text-gold-ink" />
              <span>Discounts</span>
            </button>
          </div>
        </div>
      </section>

      {/* ── Main Container ────────────────────────────────────────────── */}
      <div className="max-w-[1240px] mx-auto px-6 lg:px-[60px] py-14 lg:py-20 box-border">

        {/* ── Section 1: Annual School Fees Table ────────────────────────── */}
        <div id="fees-table" className="mb-20 scroll-mt-28">
          <div className="text-center max-w-3xl mx-auto mb-10">
            <div className="inline-flex items-center gap-2 mb-3 px-3.5 py-1 rounded-full border border-gold/30 bg-gold/10">
              <span className="text-gold text-[11px] font-bold tracking-[0.2em] uppercase">
                {ts.eyebrow || 'Transparent Tuition'}
              </span>
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-[40px] font-heading font-bold text-navy tracking-tight m-0 leading-tight">
              {ts.title || 'Annual School Fees'}
            </h2>
            <div className="inline-block mt-3 mb-3 px-4 py-1 rounded-full bg-navy/5 text-navy font-semibold text-[13px]">
              {ts.yearBadge || 'Academic Year 2027–2028'}
            </div>
            <p className="text-[16px] md:text-[17px] text-muted leading-relaxed m-0">
              {ts.subtitle || 'All fees are quoted in Indonesian Rupiah (IDR).'}
            </p>
          </div>

          {/* Interactive Filters & Payment Schedule Calculator Preview */}
          <div className="bg-white rounded-2xl p-5 md:p-6 border border-line shadow-sm mb-7 space-y-4">
            {/* Top Row: School Stage Filters */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-[12px] font-bold uppercase tracking-wider text-muted mr-1.5 flex items-center gap-1.5 shrink-0">
                  <Layers className="w-4 h-4 text-gold" /> {ts.filterLabel || 'Filter Stage:'}
                </span>
                {[
                  { id: 'all', label: ts.filterAll || 'All Stages' },
                  { id: 'preschool', label: ts.stages?.preschool || 'Preschool' },
                  { id: 'primary', label: ts.stages?.primary || 'Primary School' },
                  { id: 'middle', label: ts.stages?.middle || 'Middle School' },
                  { id: 'senior', label: ts.stages?.senior || 'Senior School' }
                ].map(f => (
                  <button
                    key={f.id}
                    onClick={() => setStageFilter(f.id)}
                    className={`px-3.5 py-1.5 rounded-xl text-[13px] font-heading font-semibold transition-all cursor-pointer ${stageFilter === f.id
                      ? 'bg-navy text-white shadow-sm'
                      : 'bg-paper text-ink/80 hover:bg-gold/15 hover:text-navy'
                      }`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>

              {/* Stage Counter */}
              <div className="text-[12.5px] font-medium text-muted shrink-0 hidden lg:block">
                {t?.utilAdm === 'Penerimaan'
                  ? `Menampilkan ${filteredRows.length} program`
                  : `Showing ${filteredRows.length} ${filteredRows.length === 1 ? 'level' : 'levels'}`}
              </div>
            </div>

            {/* Subtle Divider */}
            <div className="border-t border-line/60" />

            {/* Bottom Row: Payment Schedule / Frequency */}
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 pt-0.5">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-[12px] font-bold uppercase tracking-wider text-muted mr-1.5 flex items-center gap-1.5 shrink-0">
                  <Clock className="w-4 h-4 text-gold" /> {ts.scheduleLabel || 'View Schedule:'}
                </span>
                {[
                  { id: 'annual', label: ts.schedules?.annual || 'Annual (1x)' },
                  { id: 'quarterly', label: ts.schedules?.quarterly || 'Quarterly (4x)' },
                  { id: 'monthly', label: ts.schedules?.monthly || 'Monthly (10x)' }
                ].map(p => (
                  <button
                    key={p.id}
                    onClick={() => setPaymentPlanView(p.id)}
                    className={`px-3.5 py-1.5 rounded-xl text-[13px] font-heading font-semibold transition-all cursor-pointer ${paymentPlanView === p.id
                      ? 'bg-gold text-navy font-bold shadow-sm'
                      : 'bg-paper text-ink/75 hover:bg-gold/15 hover:text-navy'
                      }`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>

              {/* Informative helper badge on right */}
              <div className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-navy/5 text-navy text-[12px] font-semibold self-start lg:self-auto border border-navy/10">
                <Sparkles className="w-3.5 h-3.5 text-gold shrink-0" />
                <span>
                  {paymentPlanView === 'annual' &&
                    (t?.utilAdm === 'Penerimaan'
                      ? 'Paling Hemat: Diskon pelunasan tahunan 6%'
                      : 'Best Value: 6% Annual Settlement Discount Included')}
                  {paymentPlanView === 'quarterly' &&
                    (t?.utilAdm === 'Penerimaan'
                      ? '4 Kali Pembayaran per awal term'
                      : '4 Instalments billed before each term')}
                  {paymentPlanView === 'monthly' &&
                    (t?.utilAdm === 'Penerimaan'
                      ? '10 Kali cicilan bulanan jatuh tempo tgl 5'
                      : '10 Monthly instalments due by the 5th')}
                </span>
              </div>
            </div>
          </div>

          {/* Fees Table */}
          <div className="bg-white rounded-2xl border border-line shadow-md overflow-hidden mb-6">
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-left">
                <thead>
                  <tr className="bg-navy text-white text-[12.5px] font-heading font-bold uppercase tracking-wider border-b border-navy-deep">
                    <th className="py-4 px-6 md:px-8 w-[50%]">
                      {ts.columns?.stage || 'School Stage / Year Group'}
                    </th>
                    <th className="py-4 px-6 md:px-8 w-[50%] text-right">
                      {paymentPlanView === 'annual' && (ts.columns?.fee || 'Annual School Fee')}
                      {paymentPlanView === 'quarterly' && 'Quarterly Instalment (4x / Year)'}
                      {paymentPlanView === 'monthly' && 'Monthly Instalment (10x / Year)'}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-line/70 text-[15px]">
                  {filteredRows.map((item, index) => {
                    const feeDisplay = getComputedFeeDisplay(item.rawFee, item.fee);
                    return (
                      <tr
                        key={index}
                        className={`transition-colors duration-200 ${index % 2 === 0
                          ? 'bg-white hover:bg-paper/60'
                          : 'bg-[#fcfdfd] hover:bg-paper/60'
                          }`}
                      >
                        {/* Stage & Group */}
                        <td className="py-4 px-6 md:px-8 align-middle">
                          <div className="flex items-center gap-3">
                            <span className={`px-2.5 py-0.5 rounded-md text-[11px] font-bold uppercase tracking-wider border ${item.badgeColor || 'bg-navy/5 text-navy border-line'}`}>
                              {item.stage}
                            </span>
                            <span className="font-heading font-bold text-navy text-[15px] md:text-[16px]">
                              {item.group}
                            </span>
                          </div>
                        </td>

                        {/* Fee */}
                        <td className="py-4 px-6 md:px-8 text-right align-middle font-heading font-bold text-navy text-[16px] md:text-[18px]">
                          <span className="inline-block px-3 py-1 rounded-lg bg-paper text-navy font-mono">
                            {feeDisplay}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Application Fee Feature Bar inside Table footer */}
            <div className="bg-gold/10 border-t border-gold/30 px-6 md:px-8 py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-ink">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-gold text-navy-deep flex items-center justify-center shrink-0">
                  <Info className="w-4 h-4" />
                </div>
                <div>
                  <span className="font-heading font-bold text-navy text-[14px] block">
                    {appFee.title || 'Application Fee'}
                  </span>
                  <span className="text-muted text-[13px]">
                    {appFee.desc || 'A non-refundable Application Submission Fee of IDR 5,000,000 is payable once upon admission.'}
                  </span>
                </div>
              </div>
              <div className="shrink-0 font-heading font-bold text-navy text-[16px] sm:text-right pl-11 sm:pl-0">
                {appFee.amount || 'IDR 5,000,000'}
              </div>
            </div>
          </div>
        </div>

        {/* ── Section 2: Payment Options ────────────────────────────────── */}
        <div id="payment-options" className="mb-20 scroll-mt-28">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <div className="inline-flex items-center gap-2 mb-3 px-3.5 py-1 rounded-full border border-gold/30 bg-gold/10">
              <span className="text-gold text-[11px] font-bold tracking-[0.2em] uppercase">
                {po.eyebrow || 'Flexible Schedules'}
              </span>
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-[40px] font-heading font-bold text-navy tracking-tight m-0 leading-tight">
              {po.title || 'Payment Options'}
            </h2>
            <p className="text-[16px] md:text-[17px] text-muted leading-relaxed m-0">
              {po.subtitle || 'Families can choose a payment plan that best suits their needs. The school offers annual payment, quarterly payment and monthly instalment options.'}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-7">
            {(po.plans || [
              {
                title: 'Annual Payment',
                tag: 'Best Value (6% Savings)',
                desc: 'The full year’s fee is paid in a single transaction at the beginning of the academic year.'
              },
              {
                title: 'Quarterly Payment',
                tag: '4 Term Payments',
                desc: 'Fees are paid in four instalments, with due dates communicated by the school before the start of each term.'
              },
              {
                title: 'Monthly Instalments',
                tag: '10 Equal Payments',
                desc: 'Fees may be paid in 10 equal monthly instalments from the start of the academic year, with each instalment due by the 5th of the month.'
              }
            ]).map((plan, idx) => {
              const icons = [
                <CalendarCheck className="w-6 h-6 text-gold" key="1" />,
                <CreditCard className="w-6 h-6 text-gold" key="2" />,
                <Clock className="w-6 h-6 text-gold" key="3" />
              ];
              return (
                <div
                  key={idx}
                  className="bg-white rounded-2xl border border-line shadow-sm hover:border-gold/50 hover:shadow-xl transition-all duration-300 p-6 md:p-7 flex flex-col justify-between group hover:-translate-y-1.5"
                >
                  <div>
                    <div className="flex items-center justify-between mb-5">
                      <div className="w-12 h-12 rounded-xl bg-gold/15 flex items-center justify-center">
                        {icons[idx]}
                      </div>
                      <span className="px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider bg-navy/5 text-navy border border-line">
                        {plan.tag}
                      </span>
                    </div>

                    <h3 className="text-xl font-heading font-bold text-navy group-hover:text-gold-ink transition-colors mb-3 leading-snug m-0">
                      {plan.title}
                    </h3>

                    <p className="text-[14.5px] text-ink/80 leading-relaxed m-0">
                      {plan.desc}
                    </p>
                  </div>

                  <div className="pt-4 mt-6 border-t border-line/60 flex items-center gap-2 text-[12px] font-semibold text-navy">
                    <CheckCircle2 className="w-4 h-4 text-gold shrink-0" />
                    <span>Official Tuition Schedule</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* ── Section 3: Promotions & Payment Discounts ─────────────────── */}
        <div id="discounts" className="mb-10 scroll-mt-28">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <div className="inline-flex items-center gap-2 mb-3 px-3.5 py-1 rounded-full border border-gold/30 bg-gold/10">
              <span className="text-gold text-[11px] font-bold tracking-[0.2em] uppercase">
                {disc.eyebrow || 'Savings & Incentives'}
              </span>
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-[40px] font-heading font-bold text-navy tracking-tight m-0 leading-tight">
              {disc.title || 'Promotions & Payment Discounts'}
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-7">
            {/* Early Bird Promotion Card */}
            <div className="bg-white rounded-3xl p-7 lg:p-8 border border-line shadow-md hover:shadow-xl transition-all duration-300 flex flex-col justify-between relative overflow-hidden group">
              <div className="absolute top-0 right-0 w-32 h-32 bg-amber-500/5 rounded-bl-full pointer-events-none" />

              <div>
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-12 h-12 rounded-2xl bg-gold/15 text-gold-ink flex items-center justify-center shrink-0">
                    <Sparkles className="w-6 h-6 text-gold" />
                  </div>
                  <div>
                    <span className="text-[11px] font-bold tracking-[0.2em] text-gold uppercase block">
                      {disc.earlyBird?.tag || 'Special Incentive'}
                    </span>
                    <h3 className="text-xl md:text-2xl font-heading font-bold text-navy m-0">
                      {disc.earlyBird?.title || 'Early Bird Promotion'}
                    </h3>
                  </div>
                </div>

                <p className="text-[15px] text-ink/80 leading-relaxed mb-6">
                  {disc.earlyBird?.desc || 'Families enrolling during the Early Bird Promotion period may receive special discounts on school fees for the first academic year.'}
                </p>

                <div className="bg-paper/60 p-4 rounded-2xl border border-line/60 flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-gold shrink-0 mt-0.5" />
                  <span className="text-[13.5px] text-ink/85 font-medium leading-relaxed">
                    Applicable to newly admitted students across selected year groups upon timely confirmation.
                  </span>
                </div>
              </div>

              <div className="pt-6">
                <button
                  onClick={handleNavigateToContact}
                  className="inline-flex items-center gap-2 text-navy font-heading font-bold text-[13.5px] hover:text-gold transition-colors"
                >
                  <span>Inquire About Early Bird Timelines</span>
                  <ArrowRight className="w-4 h-4 text-gold group-hover:translate-x-1 transition-transform" />
                </button>
              </div>
            </div>

            {/* Payment Discounts Card */}
            <div className="bg-white rounded-3xl p-7 lg:p-8 border border-line shadow-md hover:shadow-xl transition-all duration-300 flex flex-col justify-between relative overflow-hidden group">
              <div className="absolute top-0 right-0 w-32 h-32 bg-navy/5 rounded-bl-full pointer-events-none" />

              <div>
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-12 h-12 rounded-2xl bg-navy/10 text-navy flex items-center justify-center shrink-0">
                    <Percent className="w-6 h-6 text-gold" />
                  </div>
                  <div>
                    <span className="text-[11px] font-bold tracking-[0.2em] text-gold uppercase block">
                      Tuition Settlement
                    </span>
                    <h3 className="text-xl md:text-2xl font-heading font-bold text-navy m-0">
                      {disc.discounts?.title || 'Payment Discounts'}
                    </h3>
                  </div>
                </div>

                {/* 2 Discount Highlight Rows */}
                <div className="space-y-3.5 mb-2">
                  {(disc.discounts?.items || [
                    {
                      percent: '3%',
                      title: 'Semester Settlement Discount',
                      desc: '3% discount applies to the total basic school fee if payment for the semester is settled in full during the registration process.'
                    },
                    {
                      percent: '6%',
                      title: 'Annual Full Settlement Discount',
                      desc: '6% discount applies to the total basic school fee if payment for the entire academic year is settled in full during the registration process.'
                    }
                  ]).map((item, i) => (
                    <div key={i} className="flex items-start gap-3.5 bg-paper/60 p-4 rounded-2xl border border-line/60">
                      <div className="w-12 h-12 rounded-xl bg-gold text-navy-deep font-heading font-extrabold text-[16px] flex items-center justify-center shrink-0 shadow-sm">
                        {item.percent}
                      </div>
                      <div>
                        <span className="font-heading font-bold text-navy text-[14px] block mb-1">
                          {item.title}
                        </span>
                        <p className="text-[13px] text-ink/80 leading-relaxed m-0">
                          {item.desc}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="pt-6">
                <button
                  onClick={handleNavigateToContact}
                  className="inline-flex items-center gap-2 text-navy font-heading font-bold text-[13.5px] hover:text-gold transition-colors"
                >
                  <span>Apply for Fee Settlement Discount</span>
                  <ArrowRight className="w-4 h-4 text-gold group-hover:translate-x-1 transition-transform" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* ── Section 4: What Is Not Included & Payment Method ─────────── */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-7 mb-10">
          {/* What is not included */}
          <div className="bg-white rounded-3xl p-7 lg:p-8 border border-line shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-navy/5 text-navy flex items-center justify-center shrink-0">
                <Info className="w-5 h-5 text-gold" />
              </div>
              <h3 className="text-xl font-heading font-bold text-navy m-0">
                {pol.notIncluded?.title || 'What Is Not Included'}
              </h3>
            </div>
            <p className="text-[15px] text-ink/80 leading-relaxed m-0">
              {pol.notIncluded?.desc || 'Unless otherwise specified, transport fees, books, uniforms and extracurricular activities (ECAs) are not included in the school fees.'}
            </p>
          </div>

          {/* Payment Method */}
          <div className="bg-white rounded-3xl p-7 lg:p-8 border border-line shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-navy/5 text-navy flex items-center justify-center shrink-0">
                <Building2 className="w-5 h-5 text-gold" />
              </div>
              <h3 className="text-xl font-heading font-bold text-navy m-0">
                {pol.paymentMethod?.title || 'Payment Method'}
              </h3>
            </div>
            <p className="text-[15px] text-ink/80 leading-relaxed m-0">
              {pol.paymentMethod?.desc || 'School fee payments can be made by bank transfer. Parents may contact the Finance Office for payment details, assistance with payment plans or any questions regarding school fees.'}
            </p>
          </div>
        </div>

        {/* ── Section 5: Corporate Partnerships ──────────────────────────── */}
        <div className="bg-white rounded-3xl p-8 lg:p-10 border border-line shadow-lg  relative overflow-hidden">
          <div className="absolute top-0 right-0 w-80 h-80 bg-gold/5 rounded-full blur-2xl pointer-events-none" />

          <div className="relative z-10 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
            <div className="max-w-2xl">
              <div className="inline-flex items-center gap-2 mb-3 px-3 py-1 rounded-full border border-gold/30 bg-gold/10">
                <Briefcase className="w-3.5 h-3.5 text-gold" />
                <span className="text-gold text-[11px] font-bold tracking-[0.2em] uppercase">
                  {corp.eyebrow || 'Employee Education Benefits'}
                </span>
              </div>
              <h3 className="text-2xl md:text-3xl font-heading font-bold text-navy mb-3">
                {corp.title || 'Corporate Partnerships'}
              </h3>
              <p className="text-[15px] md:text-[16px] text-ink/80 leading-relaxed m-0">
                {corp.desc || 'GMIS Jakarta works with organisations that value education and family support. If your company is looking for an education partner for employees and their families, our Marketing Team would be happy to discuss corporate partnership opportunities.'}
              </p>
            </div>

            <button
              onClick={handleNavigateToContact}
              className="btn shrink-0 inline-flex items-center justify-center gap-2.5 h-12 px-7 rounded-xl bg-navy text-white font-heading font-bold text-[13px] tracking-wider uppercase transition-all hover:bg-gold hover:text-navy-deep shadow-md"
            >
              <span>{corp.button || 'Explore Corporate Partnership Opportunities'}</span>
              <ArrowRight className="w-4 h-4 text-gold group-hover:text-navy-deep" />
            </button>
          </div>
        </div>
      </div>

      {/* ── CTA Section (PreschoolPage style) ─────────────────────────────── */}
      <div className="w-full bg-white">
        <div className="max-w-[1200px] mx-auto px-8 py-12">
          <div className="relative overflow-hidden bg-navy rounded-3xl p-12 md:p-20 text-center shadow-2xl">
            <div className="relative z-10">
              <h3 className="text-4xl md:text-5xl font-heading font-bold text-white mb-6 leading-tight">
                {cta.title || 'Have questions about school fees?'}
              </h3>
              <p className="text-white/70 text-xl max-w-2xl mx-auto mb-10">
                {cta.subtitle || 'Speak with our Admissions Team for fee clarifications, payment schedules, and tailored enrolment support.'}
              </p>
              <div className="flex flex-wrap items-center justify-center gap-4">
                <button
                  onClick={handleNavigateToContact}
                  className="bg-gold text-navy px-10 py-4 rounded-full font-bold text-lg tracking-wide hover:bg-white hover:text-navy transition-colors duration-300 cursor-pointer"
                >
                  {cta.button || 'Speak with our Admissions Team'}
                </button>
                <button
                  onClick={handleNavigateToFaqs}
                  className="bg-white/10 text-white px-10 py-4 rounded-full font-bold text-lg tracking-wide hover:bg-white/20 transition-colors duration-300 border border-white/20 cursor-pointer"
                >
                  {cta.faqButton || 'Frequently Asked Questions'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
}
